if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.colorOptionIndex = this.dataset.colorOptionIndex ? Number(this.dataset.colorOptionIndex) : null;
        this.colorValues = this.getColorValues();
        this.colorMediaGroups = this.getColorMediaGroups();

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailEvents();
        if (this.elements.thumbnails && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches)
          this.removeListSemantic();

        window.requestAnimationFrame(() => this.filterByVariant(this.getSelectedVariant()));
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails || !event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      filterByVariant(variant) {
        if (!this.hasColorFiltering()) return;

        this.bindThumbnailEvents();

        const selectedColor = this.getVariantColor(variant);
        if (!selectedColor) {
          this.showAllMedia();
          return;
        }

        const mediaItems = this.getMediaItems();
        if (!mediaItems.length) return;

        const states = mediaItems.map((item) => ({
          item,
          rawId: item.dataset.mediaRawId,
          ...this.getMediaColorState(item, selectedColor),
        }));
        const hasSelectedMedia = states.some((state) => state.matchesSelected);

        if (!hasSelectedMedia) {
          const fallbackMediaIds = this.getFallbackMediaIds(variant, mediaItems);
          if (!fallbackMediaIds.size) {
            this.showAllMedia();
            return;
          }

          this.applyVisibleMediaIds(fallbackMediaIds, variant);
          return;
        }

        const visibleMediaIds = this.getDedupeVisibleMediaIds(states);
        this.applyVisibleMediaIds(visibleMediaIds, variant);
      }

      hasColorFiltering() {
        return Number.isInteger(this.colorOptionIndex) && this.colorValues.length > 0;
      }

      getSelectedVariant() {
        const selectedVariant = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
        if (!selectedVariant?.textContent) return null;

        try {
          return JSON.parse(selectedVariant.textContent);
        } catch (error) {
          console.warn('Could not parse selected variant for media filtering', error);
          return null;
        }
      }

      getColorValues() {
        const colorValuesScript = this.querySelector('[data-product-media-color-options]');
        if (!colorValuesScript?.textContent) return [];

        try {
          const values = JSON.parse(colorValuesScript.textContent);
          return values
            .map((value) => (typeof value === 'string' ? value : value?.name || value?.value || ''))
            .filter(Boolean)
            .map((value) => ({
              raw: value,
              normalized: this.normalizeColorText(value),
              compact: this.normalizeColorText(value).replace(/\s+/g, ''),
              isNumeric: /^\d+$/.test(this.normalizeColorText(value)),
            }))
            .filter(({ normalized }) => normalized);
        } catch (error) {
          console.warn('Could not parse product media color options', error);
          return [];
        }
      }

      getColorMediaGroups() {
        const groupScript = this.querySelector('[data-product-media-color-groups]');
        if (!groupScript?.textContent) return null;

        try {
          const data = JSON.parse(groupScript.textContent);
          const mediaOrder = (data.mediaOrder || [])
            .map((media, index) => ({
              id: String(media?.id || ''),
              position: Number.isFinite(Number(media?.position)) ? Number(media.position) : index + 1,
            }))
            .filter((media) => media.id);
          const mediaPositionById = new Map(mediaOrder.map((media, index) => [media.id, index]));
          const anchors = (data.anchors || [])
            .map((anchor) => {
              const mediaId = anchor?.mediaId ? String(anchor.mediaId) : '';
              return {
                raw: anchor?.value || '',
                normalized: this.normalizeColorText(anchor?.value),
                mediaId,
                orderIndex: mediaPositionById.has(mediaId) ? mediaPositionById.get(mediaId) : null,
              };
            })
            .filter((anchor) => anchor.normalized && Number.isInteger(anchor.orderIndex))
            .sort((a, b) => a.orderIndex - b.orderIndex);

          if (!mediaOrder.length || !anchors.length) return null;

          const groups = new Map();
          anchors.forEach((anchor, index) => {
            const nextAnchor = anchors[index + 1];
            const groupIds = mediaOrder
              .slice(anchor.orderIndex, nextAnchor ? nextAnchor.orderIndex : mediaOrder.length)
              .map((media) => media.id);
            groups.set(anchor.normalized, {
              anchorId: anchor.mediaId,
              mediaIds: new Set(groupIds),
            });
          });

          return {
            mediaOrder,
            anchors,
            groups,
            hasReliableAnchors: anchors.length >= Math.min(this.colorValues.length, 2),
          };
        } catch (error) {
          console.warn('Could not parse product media color groups', error);
          return null;
        }
      }

      getVariantColor(variant) {
        const optionValue = variant?.options?.[this.colorOptionIndex];
        const normalized = this.normalizeColorText(optionValue);
        if (!normalized) return null;

        return {
          raw: optionValue,
          normalized,
          compact: normalized.replace(/\s+/g, ''),
          isNumeric: /^\d+$/.test(normalized),
        };
      }

      getMediaItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('li[data-media-id][data-media-raw-id]') || []);
      }

      getMediaColorState(item, selectedColor) {
        const variantColors = this.parseVariantColors(item.dataset.variantColors);
        const variantColorMatch = variantColors.some((color) => color === selectedColor.normalized);
        const mediaText = this.normalizeColorText([item.dataset.mediaAlt, item.dataset.mediaPreviewAlt].filter(Boolean).join(' '));
        const mediaSrcText = this.normalizeColorText(item.dataset.mediaSrc);
        const searchText = [mediaText, mediaSrcText].filter(Boolean).join(' ');
        const compactSearchText = searchText.replace(/\s+/g, '');
        const textColorMatch = this.matchesColorInMediaText(mediaText, mediaSrcText, selectedColor);
        const explicitOtherColorMatch = this.hasExplicitOtherColorMatch(mediaText, mediaSrcText, selectedColor);
        const anchorGroupMatch = this.getAnchorGroupMatch(item.dataset.mediaRawId, selectedColor);
        const matchesSelected =
          variantColorMatch || textColorMatch || (anchorGroupMatch.matchesSelected && !explicitOtherColorMatch);

        return {
          matchesSelected,
          isCommon: this.isCommonMedia(searchText, compactSearchText),
          matchesOtherColor: this.colorValues.some((color) => {
            if (color.normalized === selectedColor.normalized) return false;
            return (
              variantColors.includes(color.normalized) ||
              anchorGroupMatch.matchedOtherColor === color.normalized ||
              this.matchesColorInMediaText(mediaText, mediaSrcText, color)
            );
          }) || explicitOtherColorMatch,
        };
      }

      getAnchorGroupMatch(rawId, selectedColor) {
        if (!rawId || !this.colorMediaGroups?.hasReliableAnchors) {
          return { matchesSelected: false, matchedOtherColor: null };
        }

        const selectedGroup = this.colorMediaGroups.groups.get(selectedColor.normalized);
        if (selectedGroup?.mediaIds.has(String(rawId))) {
          return { matchesSelected: true, matchedOtherColor: null };
        }

        for (const color of this.colorValues) {
          if (color.normalized === selectedColor.normalized) continue;
          const group = this.colorMediaGroups.groups.get(color.normalized);
          if (group?.mediaIds.has(String(rawId))) {
            return { matchesSelected: false, matchedOtherColor: color.normalized };
          }
        }

        return { matchesSelected: false, matchedOtherColor: null };
      }

      hasExplicitOtherColorMatch(mediaText, mediaSrcText, selectedColor) {
        return this.getComparableColorValues().some((color) => {
          if (color.normalized === selectedColor.normalized) return false;
          return this.matchesColorInMediaText(mediaText, mediaSrcText, color);
        });
      }

      getComparableColorValues() {
        const knownColors = [
          'bej',
          'beyaz',
          'camel',
          'devetuyu',
          'ekru',
          'gri',
          'haki',
          'indigo',
          'kahve',
          'kirmizi',
          'krem',
          'lacivert',
          'mavi',
          'pembe',
          'siyah',
          'vizon',
          'yesil',
          'black',
          'blue',
          'brown',
          'cream',
          'green',
          'khaki',
          'navy',
          'white',
        ];
        const values = new Map();

        [...this.colorValues.map((color) => color.raw), ...knownColors].forEach((value) => {
          const normalized = this.normalizeColorText(value);
          if (!normalized || values.has(normalized)) return;

          values.set(normalized, {
            raw: value,
            normalized,
            compact: normalized.replace(/\s+/g, ''),
            isNumeric: /^\d+$/.test(normalized),
          });
        });

        return Array.from(values.values());
      }

      matchesColorInMediaText(mediaText, mediaSrcText, color) {
        if (!color?.normalized) return false;

        if (color.isNumeric) {
          const mediaTokens = mediaText.split(/\s+/).filter(Boolean);
          return mediaTokens[mediaTokens.length - 1] === color.normalized;
        }

        const compactMediaText = mediaText.replace(/\s+/g, '');
        const compactMediaSrcText = mediaSrcText.replace(/\s+/g, '');
        return (
          mediaText.includes(color.normalized) ||
          mediaSrcText.includes(color.normalized) ||
          compactMediaText.includes(color.compact) ||
          compactMediaSrcText.includes(color.compact)
        );
      }

      parseVariantColors(value) {
        return (value || '')
          .split('|')
          .map((color) => this.normalizeColorText(color))
          .filter(Boolean);
      }

      normalizeColorText(value) {
        return String(value || '')
          .toLocaleLowerCase('tr-TR')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/ı/g, 'i')
          .replace(/\u0131/g, 'i')
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();
      }

      isCommonMedia(searchText, compactSearchText) {
        const commonTerms = ['common', 'genel', 'all', 'size chart', 'beden tablosu', 'olcu tablosu', 'olcu'];
        return commonTerms.some((term) => {
          const normalizedTerm = this.normalizeColorText(term);
          return searchText.includes(normalizedTerm) || compactSearchText.includes(normalizedTerm.replace(/\s+/g, ''));
        });
      }

      toggleMediaItem(item, shouldHide) {
        item.hidden = shouldHide;
        item.toggleAttribute('aria-hidden', shouldHide);

        if (shouldHide) {
          item.classList.remove('is-active');
          item.querySelectorAll('video').forEach((video) => video.pause());
          window.pauseAllMedia?.();
        }
      }

      getDedupeVisibleMediaIds(states) {
        const visibleMediaIds = new Set();
        let hasVisibleVideo = false;
        const visibleVideoKeys = new Set();

        states.forEach((state) => {
          let isVisible = state.matchesSelected || state.isCommon;

          if (isVisible && this.isVideoMedia(state.item)) {
            const videoKey = this.getVideoDedupeKey(state.item);
            if (hasVisibleVideo || visibleVideoKeys.has(videoKey)) {
              isVisible = false;
            } else {
              hasVisibleVideo = true;
              visibleVideoKeys.add(videoKey);
            }
          }

          this.toggleMediaItem(state.item, !isVisible);
          if (isVisible && state.rawId) visibleMediaIds.add(state.rawId);
        });

        return visibleMediaIds;
      }

      applyVisibleMediaIds(visibleMediaIds, variant) {
        this.getMediaItems().forEach((item) => {
          this.toggleMediaItem(item, !visibleMediaIds.has(item.dataset.mediaRawId));
        });

        this.filterThumbnails(visibleMediaIds);
        this.filterModalMedia(visibleMediaIds);
        this.updateSliderState();
        this.setFilteredActiveMedia(variant, visibleMediaIds);
      }

      getFallbackMediaIds(variant, mediaItems) {
        const fallbackMediaIds = new Set();
        const featuredMediaId = variant?.featured_media?.id ? String(variant.featured_media.id) : null;

        if (featuredMediaId && mediaItems.some((item) => item.dataset.mediaRawId === featuredMediaId)) {
          fallbackMediaIds.add(featuredMediaId);
          return fallbackMediaIds;
        }

        if (mediaItems[0]?.dataset.mediaRawId) fallbackMediaIds.add(mediaItems[0].dataset.mediaRawId);
        return fallbackMediaIds;
      }

      isVideoMedia(item) {
        return ['video', 'external_video'].includes(item?.dataset?.mediaType);
      }

      getVideoDedupeKey(item) {
        const preview = this.normalizeColorText(item?.dataset?.mediaSrc || '').replace(/\s+/g, '');
        const alt = this.normalizeColorText([item?.dataset?.mediaAlt, item?.dataset?.mediaPreviewAlt].filter(Boolean).join(' '));
        return [item?.dataset?.mediaType || 'video', preview || item?.dataset?.mediaRawId, alt].filter(Boolean).join('|');
      }

      filterThumbnails(visibleMediaIds) {
        if (!this.elements.thumbnails) return;

        let visiblePosition = 1;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
          const rawId = thumbnail.dataset.mediaRawId || thumbnail.dataset.target?.split('-').pop();
          const isVisible = visibleMediaIds.has(rawId);
          thumbnail.hidden = !isVisible;
          thumbnail.toggleAttribute('aria-hidden', !isVisible);
          if (isVisible) thumbnail.dataset.mediaPosition = visiblePosition++;
        });
      }

      filterModalMedia(visibleMediaIds) {
        const modal = document.querySelector(`#ProductModal-${this.id.replace('MediaGallery-', '')}`);
        if (!modal) return;

        modal.querySelectorAll('[data-media-id]').forEach((modalMedia) => {
          const isVisible = visibleMediaIds.has(modalMedia.dataset.mediaId);
          modalMedia.hidden = !isVisible;
          modalMedia.toggleAttribute('aria-hidden', !isVisible);
          if (!isVisible) modalMedia.querySelectorAll('video').forEach((video) => video.pause());
        });
      }

      showAllMedia() {
        this.getMediaItems().forEach((item) => this.toggleMediaItem(item, false));

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail, index) => {
            thumbnail.hidden = false;
            thumbnail.removeAttribute('aria-hidden');
            thumbnail.dataset.mediaPosition = index + 1;
          });
        }

        const modal = document.querySelector(`#ProductModal-${this.id.replace('MediaGallery-', '')}`);
        modal?.querySelectorAll('[data-media-id]').forEach((modalMedia) => {
          modalMedia.hidden = false;
          modalMedia.removeAttribute('aria-hidden');
        });

        this.updateSliderState();
      }

      setFilteredActiveMedia(variant, visibleMediaIds) {
        const featuredMediaId = variant?.featured_media?.id ? String(variant.featured_media.id) : null;
        const currentActive = this.elements.viewer?.querySelector('li[data-media-id].is-active:not([hidden])');
        const featuredMedia = featuredMediaId
          ? this.elements.viewer?.querySelector(`li[data-media-raw-id="${featuredMediaId}"]:not([hidden])`)
          : null;
        const firstVisible = this.elements.viewer?.querySelector('li[data-media-id]:not([hidden])');
        const targetMedia = currentActive || featuredMedia || firstVisible;

        if (!targetMedia || !visibleMediaIds.has(targetMedia.dataset.mediaRawId)) return;

        if (!targetMedia.classList.contains('is-active')) {
          this.setActiveMedia(targetMedia.dataset.mediaId, false);
          return;
        }

        if (this.elements.thumbnails) {
          this.setActiveThumbnail(this.elements.thumbnails.querySelector(`[data-target="${targetMedia.dataset.mediaId}"]`));
        }
      }

      updateSliderState() {
        [this.elements.viewer, this.elements.thumbnails].forEach((sliderComponent) => {
          if (!sliderComponent) return;

          sliderComponent.resetPages?.();
          const visibleSlides = Array.from(sliderComponent.querySelectorAll('.slider__slide')).filter(
            (slide) => !slide.hidden
          );
          const visibleCount = visibleSlides.length;

          if (sliderComponent.currentPageElement && sliderComponent.pageTotalElement) {
            sliderComponent.currentPageElement.textContent = visibleCount ? 1 : 0;
            sliderComponent.pageTotalElement.textContent = visibleCount;
          }

          if (visibleSlides[0]) sliderComponent.slider?.scrollTo({ left: visibleSlides[0].offsetLeft });

          if (visibleCount <= 1) {
            sliderComponent.prevButton?.setAttribute('disabled', 'disabled');
            sliderComponent.nextButton?.setAttribute('disabled', 'disabled');
          }
        });
      }

      bindThumbnailEvents() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          if (mediaToSwitch.dataset.mediaGalleryBound === 'true') return;

          mediaToSwitch.dataset.mediaGalleryBound = 'true';
          mediaToSwitch
            .querySelector('button')
            ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
